import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import {
  MailConfigRequest,
  MailConfigService,
  MailConfigStatus,
} from '../services/MailConfig.service'

import { useErrorHandler } from './common/useErrorHandler'
import { useTenantOptionalService } from './common/useServiceFactory'

import { showErrorToastUnlessPermissionDenied, showToast } from '@/utils/toastMessage'

interface UseMailConfigReturn {
  status?: MailConfigStatus
  isLoadingStatus: boolean
  isSaving: boolean
  isSwitching: boolean
  isSendingTest: boolean
  loadStatus: () => void
  save: (payload: MailConfigRequest) => Promise<void>
  setEnabled: (enabled: boolean) => Promise<void>
  sendTestEmail: (to: string) => Promise<void>
}

export const useMailConfig = (): UseMailConfigReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const queryClient = useQueryClient()
  const getService = useTenantOptionalService(MailConfigService)

  const statusQuery = useQuery({
    queryKey: queryKeys.mailConfigStatus(tenantId || ''),
    queryFn: async () => {
      const service = await getService()
      return service.getStatus()
    },
    enabled: false,
    staleTime: 60 * 1000,
    retry: 1,
  })

  useErrorHandler(
    statusQuery.isError,
    'Fehler beim Laden des Mail-Server-Status.',
    'error',
    statusQuery.error
  )

  const saveMutation = useMutation({
    mutationFn: async (payload: MailConfigRequest) => {
      const service = await getService()
      return service.putMailConfig(payload)
    },
    onSuccess: (response) => {
      showToast({ severity: 'success', detail: 'Mail-Server-Einstellungen wurden gespeichert' })
      if (tenantId) {
        queryClient.setQueryData<MailConfigStatus>(
          queryKeys.mailConfigStatus(tenantId),
          (old) => ({
            mailProvider: old?.mailProvider ?? 'ses',
            configured: true,
            ...response,
          })
        )
      }
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      const detail =
        error.response?.status === 422
          ? 'Die Verbindung zum SMTP-Server konnte mit den angegebenen Daten nicht hergestellt werden.'
          : 'Fehler beim Speichern der Mail-Server-Einstellungen'

      showErrorToastUnlessPermissionDenied(error, { severity: 'error', detail })
    },
  })

  const providerMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const service = await getService()
      return service.setProvider(enabled)
    },
    onSuccess: (response) => {
      if (tenantId) {
        queryClient.setQueryData<MailConfigStatus>(
          queryKeys.mailConfigStatus(tenantId),
          (old) => ({
            mailProvider: response.mailProvider,
            configured: old?.configured ?? true,
          })
        )
      }
      showToast({
        severity: 'success',
        detail:
          response.mailProvider === 'smtp'
            ? 'Eigener Mail-Server aktiviert'
            : 'Eigener Mail-Server deaktiviert, Standard-Absender wird wieder verwendet',
      })
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: error.response?.data?.error ?? 'Fehler beim Umschalten des Mail-Servers',
      })
    },
  })

  const testMutation = useMutation({
    mutationFn: async (to: string) => {
      const service = await getService()
      return service.sendTestEmail(to)
    },
    onSuccess: () => {
      showToast({ severity: 'success', detail: 'Test-E-Mail wurde gesendet' })
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      showErrorToastUnlessPermissionDenied(error, {
        severity: 'error',
        detail: error.response?.data?.error ?? 'Test-E-Mail konnte nicht gesendet werden',
      })
    },
  })

  return {
    status: statusQuery.data,
    isLoadingStatus: statusQuery.isLoading,
    isSaving: saveMutation.isPending,
    isSwitching: providerMutation.isPending,
    isSendingTest: testMutation.isPending,
    loadStatus: () => {
      statusQuery.refetch()
    },
    save: async (payload: MailConfigRequest) => {
      await saveMutation.mutateAsync(payload)
    },
    setEnabled: async (enabled: boolean) => {
      await providerMutation.mutateAsync(enabled)
    },
    sendTestEmail: async (to: string) => {
      await testMutation.mutateAsync(to)
    },
  }
}
